import { loadVocabDeck, getCardsUpToLevel } from '../data/vocab-loader.ts';
import type { VocabCard } from '../data/vocab-types.ts';
import { FSRS, createEmptyCard, Rating, type Card, type Grade, type IPreview } from 'ts-fsrs';
import {
  loadSettings, logSession,
  allFsrsStates, putFsrsState,
} from '../data/stores.ts';
import { playWord, cancelSpeech, type PlaySource } from '../data/speech.ts';
import { loadTtsManifest, ttsUrls, type TtsManifest } from '../data/tts-loader.ts';

function hydrateCard(raw: Record<string, unknown>): Card {
  return {
    ...raw,
    due: new Date(raw['due'] as string),
    last_review: raw['last_review'] ? new Date(raw['last_review'] as string) : undefined,
  } as Card;
}

function dehydrateCard(card: Card): Record<string, unknown> {
  return {
    ...card,
    due: card.due.toISOString(),
    last_review: card.last_review?.toISOString(),
  };
}

export function renderVocabulaire(): HTMLElement {
  const root = document.createElement('section');
  root.className = 'flex flex-col items-center gap-6 max-w-2xl mx-auto';
  root.innerHTML = '<p class="text-[color:var(--color-ink-muted)] mt-12">Chargement du vocabulaire…</p>';
  initSession(root);
  return root;
}

async function initSession(root: HTMLElement): Promise<void> {
  const [deck, rawStates, settings, ttsManifest] = await Promise.all([
    loadVocabDeck(),
    allFsrsStates<Record<string, unknown>>(),
    loadSettings(),
    loadTtsManifest(),
  ]);
  const levelCards = getCardsUpToLevel(deck, settings.currentLevel);
  const fsrs = new FSRS({});
  const now = new Date();

  const states = new Map<string, Card>();
  for (const [k, v] of rawStates) states.set(k, hydrateCard(v));

  const dueReviews: VocabCard[] = [];
  const newCards: VocabCard[] = [];
  for (const vc of levelCards) {
    const s = states.get(vc.id);
    if (!s) newCards.push(vc);
    else if (s.due <= now) dueReviews.push(vc);
  }

  const queue = [...dueReviews, ...newCards.slice(0, settings.dailyVocabLimit)];

  if (queue.length === 0) {
    root.innerHTML = '';
    root.append(renderDone(states.size, levelCards.length, 0));
    return;
  }

  let idx = 0;
  let revealed = false;
  let studied = 0;

  function show(): void {
    if (idx >= queue.length) {
      root.innerHTML = '';
      root.append(renderDone(states.size, levelCards.length, studied));
      void logSession({ track: 'vocab', count: studied });
      return;
    }
    root.innerHTML = '';
    const vc = queue[idx]!;
    const card: Card = states.get(vc.id) ?? createEmptyCard(now);

    root.append(renderProgress(idx, queue.length, dueReviews.length, vc.cefr));
    root.append(renderFlashcard(vc, revealed, ttsManifest));

    if (!revealed) {
      const btn = document.createElement('button');
      btn.className = 'w-full max-w-md py-3 rounded-xl bg-[color:var(--color-fjord-600)] text-white font-medium hover:bg-[color:var(--color-fjord-700)] transition-colors';
      btn.textContent = 'Montrer la réponse';
      btn.addEventListener('click', () => { revealed = true; show(); });
      root.append(btn);
    } else {
      const result = fsrs.repeat(card, now);
      root.append(renderRatingButtons(result, async (grade) => {
        cancelSpeech();
        const updated = result[grade]!.card;
        states.set(vc.id, updated);
        await putFsrsState(vc.id, dehydrateCard(updated));
        idx++;
        studied++;
        revealed = false;
        show();
      }));
    }
  }

  show();
}

function renderProgress(idx: number, total: number, reviewCount: number, cefr: string): HTMLElement {
  const el = document.createElement('div');
  el.className = 'w-full flex items-center justify-between text-sm text-[color:var(--color-ink-muted)]';
  const pct = Math.round((idx / total) * 100);
  el.innerHTML = `
    <span>${idx + 1} / ${total} <span class="opacity-60">(${reviewCount} révisions)</span></span>
    <span class="font-mono px-2 py-0.5 rounded text-[10px]" style="background: var(--tint-sage-bg); color: var(--tint-sage-ink);">${cefr}</span>
  `;
  const bar = document.createElement('div');
  bar.className = 'w-full h-1 rounded-full bg-[color:var(--color-border)] mt-2';
  bar.innerHTML = `<div class="h-full rounded-full bg-[color:var(--color-fjord-500)] transition-all" style="width:${pct}%"></div>`;
  const wrap = document.createElement('div');
  wrap.className = 'w-full';
  wrap.append(el, bar);
  return wrap;
}

function renderFlashcard(vc: VocabCard, revealed: boolean, ttsManifest: TtsManifest): HTMLElement {
  const card = document.createElement('div');
  card.className = 'w-full rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-elevated)] p-8 flex flex-col items-center gap-3 min-h-[320px] justify-center';

  const ttsAudio = ttsUrls(ttsManifest, vc.id);
  const initialBadge = badgeForSource(
    (vc.audio?.length ?? 0) > 0 ? 'human' : ttsAudio.length > 0 ? 'neural' : 'tts'
  );

  card.innerHTML = `
    <div class="flex items-center gap-3">
      <h2 class="text-4xl font-semibold tracking-tight">${vc.word}</h2>
      <button data-play class="w-10 h-10 rounded-full bg-[color:var(--color-fjord-600)] text-white hover:bg-[color:var(--color-fjord-700)] transition-colors flex items-center justify-center text-lg" aria-label="Écouter le mot">▶</button>
    </div>
    ${vc.ipa ? `<p class="text-lg text-[color:var(--color-ink-muted)] font-mono">${vc.ipa}</p>` : ''}
    <div class="flex items-center gap-2">
      <span class="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded" style="background: var(--tint-sage-bg); color: var(--tint-sage-ink);">${vc.pos}${vc.gender ? ` (${vc.gender})` : ''}</span>
      <span data-source-badge>${initialBadge}</span>
    </div>
  `;

  const playBtn = card.querySelector('[data-play]') as HTMLButtonElement;
  const badgeEl = card.querySelector('[data-source-badge]') as HTMLElement;
  let playCount = 0;
  const doPlay = (): void => {
    playBtn.disabled = true;
    playBtn.innerHTML = '⏸';
    void playWord(vc.word, { audio: vc.audio, ttsAudio, variant: playCount++ }).then((src) => {
      badgeEl.innerHTML = badgeForSource(src);
      playBtn.disabled = false;
      playBtn.innerHTML = '▶';
    });
  };
  playBtn.addEventListener('click', doPlay);
  setTimeout(doPlay, 250);

  if (revealed) {
    const hr = document.createElement('hr');
    hr.className = 'w-full border-[color:var(--color-border)] my-2';
    card.append(hr);

    for (const g of vc.glosses.slice(0, 3)) {
      const p = document.createElement('p');
      p.className = 'text-lg text-center';
      p.textContent = g;
      card.append(p);
    }

    for (const ex of vc.examples.slice(0, 2)) {
      const d = document.createElement('div');
      d.className = 'w-full text-sm rounded-lg bg-[color:var(--color-surface)] p-3 mt-2';
      d.innerHTML = `<p class="font-medium">${ex.nb}</p><p class="text-[color:var(--color-ink-muted)]">${ex.fr}</p>`;
      card.append(d);
    }
  }

  return card;
}

function badgeForSource(src: PlaySource): string {
  switch (src) {
    case 'human':  return '<span class="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded" style="background: var(--tint-mint-bg); color: var(--tint-mint-ink);">🎙️ voix humaine NTNU</span>';
    case 'neural': return '<span class="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded" style="background: var(--tint-butter-bg); color: var(--tint-butter-ink);">🎤 neural Azure</span>';
    case 'tts':    return '<span class="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded" style="background: var(--tint-peach-bg); color: var(--tint-peach-ink);">🔉 synthèse système</span>';
    case 'none':   return '<span class="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded" style="background: var(--tint-rose-bg); color: var(--tint-rose-ink);">audio indisponible</span>';
  }
}

function renderRatingButtons(
  result: IPreview,
  onRate: (g: Grade) => void,
): HTMLElement {
  const row = document.createElement('div');
  row.className = 'flex gap-2 w-full';
  const buttons: { grade: Grade; label: string; bg: string; ink: string }[] = [
    { grade: Rating.Again as Grade, label: 'À revoir',  bg: 'var(--tint-rose-bg)',   ink: 'var(--tint-rose-ink)' },
    { grade: Rating.Hard  as Grade, label: 'Difficile',  bg: 'var(--tint-peach-bg)',  ink: 'var(--tint-peach-ink)' },
    { grade: Rating.Good  as Grade, label: 'Bien',        bg: 'var(--tint-sage-bg)',   ink: 'var(--tint-sage-ink)' },
    { grade: Rating.Easy  as Grade, label: 'Facile',      bg: 'var(--tint-mint-bg)',   ink: 'var(--tint-mint-ink)' },
  ];
  for (const { grade, label, bg, ink } of buttons) {
    const next = result[grade]!.card;
    const days = next.scheduled_days;
    const interval = days > 0 ? `${days}j` : '<1j';
    const btn = document.createElement('button');
    btn.className = 'flex-1 py-3 rounded-xl text-sm font-medium transition-opacity hover:opacity-80';
    btn.style.background = bg;
    btn.style.color = ink;
    btn.innerHTML = `${label}<br><span class="text-[11px] opacity-70">${interval}</span>`;
    btn.addEventListener('click', () => onRate(grade));
    row.append(btn);
  }
  return row;
}

function renderDone(studiedTotal: number, total: number, sessionCount: number): HTMLElement {
  const el = document.createElement('div');
  el.className = 'flex flex-col items-center gap-4 mt-12';
  el.innerHTML = `
    <div class="w-16 h-16 rounded-full bg-[color:var(--color-sage)] text-white flex items-center justify-center text-2xl">✓</div>
    <h2 class="text-2xl font-semibold">Session terminée</h2>
    ${sessionCount > 0
      ? `<p class="text-[color:var(--color-ink-muted)]">${sessionCount} carte${sessionCount > 1 ? 's' : ''} étudiée${sessionCount > 1 ? 's' : ''} cette session.</p>`
      : '<p class="text-[color:var(--color-ink-muted)]">Aucune carte à revoir aujourd’hui. Revenez demain&nbsp;!</p>'}
    <p class="text-xs text-[color:var(--color-ink-muted)]">Au total : ${studiedTotal} cartes apprises sur ${total} disponibles à votre niveau.</p>
    <a href="#/accueil" class="text-sm text-[color:var(--color-fjord-600)] hover:underline">← Retour à l'accueil</a>
  `;
  return el;
}
